import { useCallback, useEffect } from "react";
import { Socket } from "socket.io-client";
import { useQueryClient, InfiniteData } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { QueryKeys } from "../utils";

import { useProfileQuery } from "../user/profile";

import { ConversationMessage } from "./user-chat";
import { ConversationSummary } from "./user-conversation";
import { getAuthedSocket } from "@/realtime/getSocket";

let socket: Socket | null = null;

export function useChatSocket(conversationId: string) {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const { profile } = useProfileQuery();

  // Listen for new messages
  const handleNewMessage = useCallback(
    (message: ConversationMessage) => {
      // Update message
      qc.setQueryData(
        QueryKeys.chatMessage(conversationId),
        (oldData?: InfiniteData<ConversationMessage[]>) => {
          if (
            // If we have no data
            !oldData ||
            // Or the message we received is already present
            oldData.pages.some((page) =>
              page.some(
                ({ id, clientId }) =>
                  id === message.id ||
                  (message.clientId && clientId === message.clientId),
              ),
            )
          ) {
            // Make no changes
            return oldData;
          }

          return {
            ...oldData,
            pages: [
              [message, ...(oldData.pages[0] ?? [])],
              ...oldData.pages.slice(1),
            ],
          };
        },
      );

      // Update lastMessage in chatConversations summary (if newer)
      qc.setQueryData<ConversationSummary[]>(
        QueryKeys.chatConversations,
        (oldConversations) =>
          // Map over conversations
          oldConversations?.map((conversation) =>
            // If we find the same conversation
            conversation.id === conversationId &&
            // And we have a more recent lastMessage
            (!conversation.lastMessage?.createdAt ||
              message.createdAt > conversation.lastMessage.createdAt)
              ? // Update the lastMessage field
                message.senderId === profile?.id
                ? // Also update the lastSeenAt field if the user was the sender
                  {
                    ...conversation,
                    lastMessage: message,
                    lastSeenMessageId: message.id,
                    lastSeenAt: message.createdAt,
                  }
                : {
                    ...conversation,
                    lastMessage: message,
                  }
              : // Otherwise, make no change
                conversation,
          ),
      );
    },
    [conversationId, qc, profile?.id],
  );

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    // Track if we've unmounted to avoid race conditions with IIFE
    let isMounted = true;

    void (async () => {
      socket = await getAuthedSocket(getAccessTokenSilently);

      if (!isMounted) {
        return;
      }

      // Join room for this conversation
      socket.emit("conversation:subscribe", conversationId);

      socket.on("conversation:new_message", handleNewMessage);
    })();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      socket?.emit("conversation:unsubscribe", conversationId);
      socket?.off("conversation:new_message", handleNewMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, qc]);
}
